"use client";

import { useCallback, useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Bell, Check, ChevronDown, Home, Paintbrush, RotateCcw, Save, Settings, Tag, Undo2, X } from "lucide-react";
import { Alert } from "@/components/Alert";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { Dialog } from "@/components/Dialog";
import { Drawer } from "@/components/Drawer";
import { Input } from "@/components/Input";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Menu, MenuItem } from "@/components/Menu";
import { NavigationItem } from "@/components/NavigationItem";
import { Popover } from "@/components/Popover";
import { Radio } from "@/components/Radio";
import { Skeleton } from "@/components/Skeleton";
import { Tab, TabList, TabPanel, Tabs } from "@/components/Tabs";
import {
  DEFAULT_THEME_APPEARANCE,
  HEADER_COLOR_TOKENS,
  SIDEBAR_COLOR_TOKENS,
  THEME_APPEARANCE_CSS_VARIABLES,
  THEME_APPEARANCE_STORAGE_KEY,
  THEME_APPEARANCE_STORAGE_KEY_V2,
  comporThemeAppearanceValores,
  lerThemeAppearanceOverrides,
  mesclarThemeAppearanceOverrides,
  normalizarThemeAppearanceOverrides,
  removerThemeAppearanceOverride,
  solicitarCancelarThemeAppearance,
  solicitarPreviewThemeAppearance,
  solicitarRestaurarThemeAppearance,
  solicitarSalvarThemeAppearance,
  type ThemeAppearanceOverrides,
  type ThemeAppearanceTokens,
  type ThemeAppearanceToken,
} from "@/lib/theme-appearance";

type ColorToken = Exclude<ThemeAppearanceToken, "font-family" | "font-size-base" | "radius" | "button-disabled-opacity" | "button-radius" | "choice-disabled-opacity" | "floating-shadow">;
type ButtonValueToken = "button-disabled-opacity" | "button-radius";
type ValidationErrors = Partial<Record<ThemeAppearanceToken, string>>;

type ColorField = {
  token: ColorToken;
  label: string;
  description: string;
};

const COLOR_GROUPS: { title: string; fields: ColorField[] }[] = [
  {
    title: "Cores globais",
    fields: [
      { token: "primary", label: "Cor principal", description: "Ações e destaques principais." },
      { token: "primary-hover", label: "Cor principal — ao passar o cursor", description: "Estado ao passar o cursor." },
      { token: "secondary", label: "Cor secundária", description: "Apoio visual, sem substituir a principal." },
    ],
  },
  {
    title: "Estrutura e superfícies",
    fields: [
      { token: "background", label: "Fundo da aplicação", description: "Base das telas e áreas externas." },
      { token: "surface", label: "Superfície/card", description: "Cards e blocos de conteúdo." },
      { token: "sidebar", label: "Barra lateral", description: "Navegação lateral no desktop." },
    ],
  },
  {
    title: "Texto e estados",
    fields: [
      { token: "text", label: "Texto principal", description: "Títulos e conteúdo principal." },
      { token: "text-muted", label: "Texto secundário", description: "Descrições e informações auxiliares." },
      { token: "border", label: "Borda", description: "Divisores, campos e contornos." },
      { token: "success", label: "Sucesso", description: "Confirmações e estados positivos." },
      { token: "warning", label: "Alerta", description: "Avisos que pedem atenção." },
      { token: "danger", label: "Erro", description: "Erros e ações destrutivas." },
      { token: "info", label: "Informação", description: "Mensagens informativas." },
    ],
  },
];

const BUTTON_COLOR_FIELDS: ColorField[] = [
  { token: "button-primary-bg", label: "Principal — fundo", description: "Fundo das ações principais." },
  { token: "button-primary-hover", label: "Principal — ao passar o cursor", description: "Fundo ao passar o cursor." },
  { token: "button-primary-text", label: "Principal — texto", description: "Texto das ações principais." },
  { token: "button-secondary-bg", label: "Secundário — fundo", description: "Fundo das ações secundárias." },
  { token: "button-secondary-hover", label: "Secundário — ao passar o cursor", description: "Fundo das ações secundárias ao passar o cursor." },
  { token: "button-secondary-text", label: "Secundário — texto", description: "Texto das ações secundárias." },
  { token: "button-outline-bg", label: "Contorno — fundo", description: "Fundo dos botões de contorno." },
  { token: "button-outline-hover", label: "Contorno — ao passar o cursor", description: "Fundo dos botões de contorno ao passar o cursor." },
  { token: "button-outline-text", label: "Contorno — texto", description: "Texto dos botões de contorno e transparentes." },
  { token: "button-outline-border", label: "Contorno — borda", description: "Borda dos botões de contorno." },
  { token: "button-ghost-hover", label: "Transparente — ao passar o cursor", description: "Fundo ao passar o cursor." },
  { token: "button-danger-bg", label: "Destrutivo — fundo", description: "Fundo das ações destrutivas." },
  { token: "button-danger-hover", label: "Destrutivo — ao passar o cursor", description: "Fundo das ações destrutivas ao passar o cursor." },
  { token: "button-danger-text", label: "Destrutivo — texto", description: "Texto das ações destrutivas." },
  { token: "button-focus-ring", label: "Anel de foco", description: "Cor do foco visível." },
];

const BUTTON_VALUE_FIELDS: { token: ButtonValueToken; label: string; description: string; placeholder: string }[] = [
  { token: "button-disabled-opacity", label: "Opacidade desabilitado", description: "Valor entre 0 e 1 para ações indisponíveis.", placeholder: "0.5" },
  { token: "button-radius", label: "Arredondamento dos botões", description: "Exemplo: 0.75rem.", placeholder: "0.75rem" },
];

const SIDEBAR_COLOR_FIELDS: ColorField[] = [
  { token: "sidebar-bg", label: "Barra lateral — fundo", description: "Fundo da navegação lateral." },
  { token: "sidebar-text", label: "Barra lateral — texto", description: "Texto dos itens não ativos." },
  { token: "sidebar-icon", label: "Barra lateral — ícone", description: "Ícones dos itens não ativos." },
  { token: "sidebar-hover-bg", label: "Item — ao passar o cursor", description: "Fundo ao passar o cursor." },
  { token: "sidebar-active-bg", label: "Item ativo — fundo", description: "Fundo do item selecionado." },
  { token: "sidebar-active-text", label: "Item ativo — texto", description: "Texto do item selecionado." },
  { token: "sidebar-active-icon", label: "Item ativo — ícone", description: "Ícone do item selecionado." },
  { token: "sidebar-border", label: "Barra lateral — borda", description: "Borda externa da navegação." },
  { token: "sidebar-divider", label: "Barra lateral — divisor", description: "Divisores internos da navegação." },
  { token: "sidebar-brand-bg", label: "Marca — fundo", description: "Fundo da identificação da marca." },
  { token: "sidebar-brand-text", label: "Marca — texto", description: "Texto da identificação da marca." },
  { token: "sidebar-badge-bg", label: "Selo — fundo", description: "Fundo de indicadores e selos." },
  { token: "sidebar-badge-text", label: "Selo — texto", description: "Texto de selos com conteúdo." },
];

const HEADER_COLOR_FIELDS: ColorField[] = [
  { token: "header-bg", label: "Cabeçalho — fundo", description: "Fundo da barra superior." },
  { token: "header-text", label: "Cabeçalho — texto", description: "Nome e textos principais." },
  { token: "header-text-muted", label: "Cabeçalho — texto secundário", description: "Saudação e textos auxiliares." },
  { token: "header-icon", label: "Cabeçalho — ícone", description: "Ícones da barra superior." },
  { token: "header-border", label: "Cabeçalho — borda", description: "Borda ou divisor da barra." },
  { token: "header-action-bg", label: "Ação — fundo", description: "Fundo dos controles do cabeçalho." },
  { token: "header-action-hover", label: "Ação — ao passar o cursor", description: "Fundo dos controles ao passar o cursor." },
  { token: "header-action-icon", label: "Ação — ícone", description: "Ícones dos controles do cabeçalho." },
];

const CARD_COLOR_FIELDS: ColorField[] = [
  { token: "card-parent-bg", label: "Fundo — Card Pai", description: "Fundo dos containers principais de cards." },
  { token: "card-child-bg", label: "Fundo — Card Filho", description: "Fundo dos cards internos." },
  { token: "card-accent", label: "Cor de destaque", description: "Cor compartilhada de ícones e destaques dos cards." },
  { token: "card-icon-bg", label: "Fundo dos ícones", description: "Fundo dos ícones exibidos nos cards." },
  { token: "card-value-color", label: "Cor dos números/valores", description: "Cor dos valores e números destacados." },
];

const TABS_COLOR_FIELDS: ColorField[] = [
  { token: "tabs-bg", label: "Fundo", description: "Fundo da área de abas." },
  { token: "tabs-text", label: "Texto", description: "Texto das abas inativas." },
  { token: "tabs-active-text", label: "Texto ativo", description: "Texto e indicador da aba selecionada." },
  { token: "tabs-hover", label: "Ao passar o cursor", description: "Cor ao passar o cursor sobre uma aba." },
  { token: "tabs-border", label: "Borda", description: "Borda e divisor das abas." },
  { token: "tabs-focus-ring", label: "Anel de foco", description: "Anel de foco visível das abas." },
];

const FLOATING_COLOR_FIELDS: ColorField[] = [
  { token: "floating-bg", label: "Fundo", description: "Fundo de menus e painéis flutuantes." },
  { token: "floating-text", label: "Texto", description: "Texto de menus e painéis flutuantes." },
  { token: "floating-border", label: "Borda", description: "Borda das superfícies flutuantes." },
  { token: "floating-hover", label: "Ao passar o cursor", description: "Fundo ao passar sobre uma ação flutuante." },
  { token: "floating-focus-ring", label: "Anel de foco", description: "Anel de foco dos itens flutuantes." },
];

const FLOATING_VALUE_FIELDS: { token: "floating-shadow"; label: string; description: string; placeholder: string }[] = [
  { token: "floating-shadow", label: "Sombra", description: "Sombra CSS simples da superfície flutuante.", placeholder: "0 18px 40px rgba(15, 23, 42, 0.14)" },
];

const OVERLAY_COLOR_FIELDS: ColorField[] = [
  { token: "overlay-surface-bg", label: "Fundo da janela/painel", description: "Fundo compartilhado por janelas e painéis laterais." },
  { token: "overlay-surface-text", label: "Texto", description: "Texto das superfícies de janelas e painéis laterais." },
  { token: "overlay-surface-border", label: "Borda", description: "Borda das superfícies de janelas e painéis laterais." },
  { token: "overlay-backdrop", label: "Fundo do overlay", description: "Fundo da camada atrás da janela ou painel." },
];

const OVERLAY_VALUE_FIELDS: { token: "overlay-surface-shadow"; label: string; description: string; placeholder: string }[] = [
  { token: "overlay-surface-shadow", label: "Sombra", description: "Sombra CSS das superfícies de janelas e painéis laterais.", placeholder: "0 18px 40px rgba(15, 23, 42, 0.14)" },
];

const FIELD_COLOR_FIELDS: ColorField[] = [
  { token: "field-bg", label: "Fundo", description: "Fundo dos campos migrados." },
  { token: "field-text", label: "Texto", description: "Texto principal dos campos." },
  { token: "field-placeholder", label: "Texto de orientação", description: "Texto de orientação dos campos vazios." },
  { token: "field-border", label: "Borda", description: "Borda padrão dos campos." },
  { token: "field-border-hover", label: "Borda — ao passar o cursor", description: "Borda ao passar o cursor." },
  { token: "field-border-focus", label: "Borda de foco", description: "Borda do foco visível." },
  { token: "field-focus-ring", label: "Anel de foco", description: "Anel de foco visível dos campos." },
  { token: "field-disabled-bg", label: "Fundo desabilitado", description: "Fundo de campos desabilitados." },
  { token: "field-disabled-text", label: "Texto desabilitado", description: "Texto de campos desabilitados." },
  { token: "field-readonly-bg", label: "Fundo somente leitura", description: "Fundo de campos somente leitura." },
  { token: "field-error", label: "Erro", description: "Borda e foco de campos inválidos." },
  { token: "field-icon", label: "Ícone", description: "Cor de ícones associados a campos." },
];

const CHOICE_COLOR_FIELDS: ColorField[] = [
  { token: "choice-bg", label: "Fundo", description: "Fundo de Checkbox e Radio desmarcados." },
  { token: "choice-border", label: "Borda", description: "Borda de Checkbox e Radio." },
  { token: "choice-checked-bg", label: "Fundo selecionado", description: "Fundo de Checkbox e Radio marcados." },
  { token: "choice-indicator", label: "Indicador selecionado", description: "Cor do indicador de seleção." },
  { token: "choice-focus-ring", label: "Anel de foco", description: "Anel de foco visível." },
  { token: "choice-error", label: "Cor de erro", description: "Borda de Checkbox e Radio inválidos." },
];

const BADGE_TYPE_COLOR_FIELDS: ColorField[] = [
  { token: "badge-type-paid-bg", label: "Pago — fundo", description: "Fundo do tipo Pago." },
  { token: "badge-type-paid-text", label: "Pago — texto", description: "Texto do tipo Pago." },
  { token: "badge-type-personalized-bg", label: "Personalizada — fundo", description: "Fundo de etiquetas personalizadas." },
  { token: "badge-type-personalized-text", label: "Personalizada — texto", description: "Texto de etiquetas personalizadas." },
  { token: "badge-type-cold-bg", label: "Frio — fundo", description: "Fundo do tipo Frio." },
  { token: "badge-type-cold-text", label: "Frio — texto", description: "Texto do tipo Frio." },
  { token: "badge-type-lost-bg", label: "Perdido — fundo", description: "Fundo do tipo Perdido." },
  { token: "badge-type-lost-text", label: "Perdido — texto", description: "Texto do tipo Perdido." },
];

const BADGE_COLOR_FIELDS: ColorField[] = [
  { token: "badge-neutral-bg", label: "Neutro — fundo", description: "Fundo dos selos sem semântica de estado." },
  { token: "badge-neutral-text", label: "Neutro — texto", description: "Texto dos selos neutros." },
  { token: "badge-neutral-border", label: "Neutro — borda", description: "Borda dos selos neutros." },
  { token: "badge-info-bg", label: "Informação — fundo", description: "Fundo dos selos informativos." },
  { token: "badge-info-text", label: "Informação — texto", description: "Texto dos selos informativos." },
  { token: "badge-info-border", label: "Informação — borda", description: "Borda dos selos informativos." },
  { token: "badge-success-bg", label: "Sucesso — fundo", description: "Fundo dos selos de sucesso." },
  { token: "badge-success-text", label: "Sucesso — texto", description: "Texto dos selos de sucesso." },
  { token: "badge-success-border", label: "Sucesso — borda", description: "Borda dos selos de sucesso." },
  { token: "badge-warning-bg", label: "Aviso — fundo", description: "Fundo dos selos de aviso." },
  { token: "badge-warning-text", label: "Aviso — texto", description: "Texto dos selos de aviso." },
  { token: "badge-warning-border", label: "Aviso — borda", description: "Borda dos selos de aviso." },
  { token: "badge-danger-bg", label: "Erro — fundo", description: "Fundo dos selos de erro." },
  { token: "badge-danger-text", label: "Erro — texto", description: "Texto dos selos de erro." },
  { token: "badge-danger-border", label: "Erro — borda", description: "Borda dos selos de erro." },
];

const ALERT_COLOR_FIELDS: ColorField[] = [
  { token: "alert-info-bg", label: "Informação — fundo", description: "Fundo dos alertas informativos." },
  { token: "alert-info-text", label: "Informação — texto", description: "Texto dos alertas informativos." },
  { token: "alert-info-border", label: "Informação — borda", description: "Borda dos alertas informativos." },
  { token: "alert-success-bg", label: "Sucesso — fundo", description: "Fundo dos alertas de sucesso." },
  { token: "alert-success-text", label: "Sucesso — texto", description: "Texto dos alertas de sucesso." },
  { token: "alert-success-border", label: "Sucesso — borda", description: "Borda dos alertas de sucesso." },
  { token: "alert-warning-bg", label: "Aviso — fundo", description: "Fundo dos alertas de aviso." },
  { token: "alert-warning-text", label: "Aviso — texto", description: "Texto dos alertas de aviso." },
  { token: "alert-warning-border", label: "Aviso — borda", description: "Borda dos alertas de aviso." },
  { token: "alert-danger-bg", label: "Erro — fundo", description: "Fundo dos alertas de erro." },
  { token: "alert-danger-text", label: "Erro — texto", description: "Texto dos alertas de erro." },
  { token: "alert-danger-border", label: "Erro — borda", description: "Borda dos alertas de erro." },
];

const LOADING_COLOR_FIELDS: ColorField[] = [
  { token: "loading-skeleton-bg", label: "Fundo do indicador de carregamento", description: "Base do indicador visual de carregamento." },
  { token: "loading-skeleton-highlight", label: "Destaque do indicador", description: "Faixa de brilho do carregamento." },
  { token: "loading-spinner", label: "Cor do indicador", description: "Cor dos indicadores giratórios." },
  { token: "loading-text", label: "Cor do texto", description: "Texto das mensagens simples de carregamento." },
];

const HEADER_TOKENS = new Set<ThemeAppearanceToken>(HEADER_COLOR_TOKENS);
const SIDEBAR_TOKENS = new Set<ThemeAppearanceToken>(SIDEBAR_COLOR_TOKENS);

function elementoVisivel(elemento: HTMLElement | null) {
  if (!elemento) return false;
  for (let atual: HTMLElement | null = elemento; atual; atual = atual.parentElement) {
    const estilo = window.getComputedStyle(atual);
    if (estilo.display === "none" || estilo.visibility === "hidden") return false;
  }
  return elemento.getClientRects().length > 0;
}

function primeiroElementoVisivel(...seletores: string[]) {
  for (const seletor of seletores) {
    for (const elemento of document.querySelectorAll<HTMLElement>(seletor)) {
      if (elementoVisivel(elemento)) return elemento;
    }
  }
  return null;
}

function escopoEfetivo(token: ThemeAppearanceToken) {
  if (HEADER_TOKENS.has(token)) {
    return primeiroElementoVisivel('[data-user-header="true"]', '[data-user-header-shell="true"]');
  }
  if (SIDEBAR_TOKENS.has(token)) {
    return primeiroElementoVisivel('[data-user-desktop-sidebar="true"]', '[data-user-mobile-navigation="true"]');
  }
  return primeiroElementoVisivel(".bp-desktop-app") ?? document.documentElement;
}

function lerValoresEfetivos(
  anteriores: ThemeAppearanceTokens | null,
  tokensProtegidos: Set<ThemeAppearanceToken>,
): ThemeAppearanceTokens {
  const valores = { ...(anteriores ?? DEFAULT_THEME_APPEARANCE.tokens) };
  for (const token of Object.keys(THEME_APPEARANCE_CSS_VARIABLES) as ThemeAppearanceToken[]) {
    if (tokensProtegidos.has(token) && anteriores?.[token]) continue;
    const escopo = escopoEfetivo(token) ?? document.documentElement;
    const valor = window.getComputedStyle(escopo).getPropertyValue(THEME_APPEARANCE_CSS_VARIABLES[token]).trim();
    if (valor) valores[token] = valor;
  }
  return valores;
}

function valorParaSeletorDeCor(valor: string) {
  return /^#[0-9a-f]{6}$/i.test(valor) ? valor : "#000000";
}

function mensagemValidacao(token: ThemeAppearanceToken) {
  if (token === "button-disabled-opacity" || token === "choice-disabled-opacity") return "Use um número entre 0 e 1.";
  if (token === "font-size-base" || token === "radius" || token === "button-radius" || token === "field-radius") {
    return "Use uma medida CSS permitida, como 1rem.";
  }
  if (token === "font-family") return "Use uma família tipográfica válida.";
  return "Use uma cor CSS válida.";
}

type CorHsl = { h: number; s: number; l: number; a: number; formato: "hex" | "rgb" };

function componenteRgb(valor: string) {
  const numero = valor.endsWith("%") ? Number.parseFloat(valor) * 2.55 : Number.parseFloat(valor);
  return Math.round(Math.min(255, Math.max(0, numero)));
}

function interpretarCor(valor: string): CorHsl | null {
  const normalizado = valor.trim();
  const hex = normalizado.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    const partes = hex[1].length === 3
      ? hex[1].split("").map((parte) => `${parte}${parte}`)
      : hex[1].match(/.{2}/g);
    if (!partes || (partes.length !== 3 && partes.length !== 4)) return null;
    const [r, g, b] = partes.slice(0, 3).map((parte) => Number.parseInt(parte, 16) / 255);
    const a = partes[3] ? Number.parseInt(partes[3], 16) / 255 : 1;
    return { ...rgbParaHsl(r, g, b), a, formato: "hex" };
  }

  const rgb = normalizado.match(/^rgba?\((.+)\)$/i);
  if (!rgb) return null;
  const partes = rgb[1].replace(/\s*\/\s*/, ",").split(/[\s,]+/).filter(Boolean);
  if (partes.length < 3) return null;
  const r = componenteRgb(partes[0]) / 255;
  const g = componenteRgb(partes[1]) / 255;
  const b = componenteRgb(partes[2]) / 255;
  const a = partes[3] ? Math.min(1, Math.max(0, Number.parseFloat(partes[3].replace("%", "")) / (partes[3].endsWith("%") ? 100 : 1))) : 1;
  return { ...rgbParaHsl(r, g, b), a, formato: "rgb" };
}

function rgbParaHsl(r: number, g: number, b: number) {
  const maior = Math.max(r, g, b);
  const menor = Math.min(r, g, b);
  const delta = maior - menor;
  const l = (maior + menor) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (delta !== 0) {
    if (maior === r) h = 60 * (((g - b) / delta) % 6);
    else if (maior === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  return { h: h < 0 ? h + 360 : h, s: s * 100, l: l * 100 };
}

function hslParaRgb({ h, s, l }: Pick<CorHsl, "h" | "s" | "l">) {
  const saturacao = s / 100;
  const luminosidade = l / 100;
  const c = (1 - Math.abs(2 * luminosidade - 1)) * saturacao;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = luminosidade - c / 2;
  const base = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return base.map((valor) => Math.round((valor + m) * 255));
}

function ajustarLuminosidadeCor(valor: string, luminosidade: number) {
  const cor = interpretarCor(valor);
  if (!cor) return valor;
  const [r, g, b] = hslParaRgb({ ...cor, l: luminosidade });
  if (cor.formato === "hex" && cor.a === 1) return `#${[r, g, b].map((parte) => parte.toString(16).padStart(2, "0")).join("")}`;
  return `rgba(${r}, ${g}, ${b}, ${Number(cor.a.toFixed(2))})`;
}

function luminosidadeDaCor(valor: string) {
  return interpretarCor(valor)?.l ?? 50;
}

function EditorModulo({
  title,
  preview,
  open = false,
  children,
}: {
  title: string;
  preview?: ReactNode;
  open?: boolean;
  children: ReactNode;
}) {
  return (
    <details
      name="theme-appearance-section"
      open={open}
      className="group"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-1 py-3.5 text-sm font-bold text-[var(--fz-color-text)] transition-colors hover:text-[var(--fz-color-primary)]">
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[var(--fz-color-text-muted)] transition-transform group-open:rotate-180" />
      </summary>
      <div className="space-y-4 border-t border-[var(--fz-color-border)] px-0 py-3">
        {preview}
        {children}
      </div>
    </details>
  );
}

function CampoCor({
  field,
  value,
  error,
  onChange,
  onSave,
  onReset,
  saveDisabled = false,
  resetDisabled = false,
}: {
  field: ColorField;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  onReset?: () => void;
  saveDisabled?: boolean;
  resetDisabled?: boolean;
}) {
  const corInterpretavel = Boolean(interpretarCor(value));

  return (
    <div className="mx-1 space-y-3 rounded-2xl border border-[var(--fz-color-border)] bg-[var(--fz-color-surface)] p-3 shadow-[0_6px_20px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_10px_26px_rgba(15,23,42,0.08)]">
      <div className="flex items-start gap-3">
        <span className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[var(--fz-color-border)] bg-[var(--fz-color-background)] shadow-inner">
          <span className="absolute inset-0" style={{ backgroundColor: value }} />
          <input
            aria-label={`${field.label} — seletor de cor`}
            type="color"
            value={valorParaSeletorDeCor(value)}
            onChange={(event) => onChange(event.target.value)}
            aria-invalid={error ? true : undefined}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--fz-color-primary)]"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm font-bold text-[var(--fz-color-text)]">{field.label}</span>
          <span className="mt-0.5 block text-xs leading-relaxed text-[var(--fz-color-text-muted)]">{field.description}</span>
        </span>
      </div>

      <div>
        <span className="mb-1.5 flex items-center justify-between text-[10px] font-semibold text-[var(--fz-color-text-muted)]">
          <span>Mais claro</span>
          <span>Mais escuro</span>
        </span>
        <input
          aria-label={`${field.label} — luminosidade`}
          type="range"
          min="10"
          max="90"
          step="1"
          value={luminosidadeDaCor(value)}
          disabled={!corInterpretavel}
          onChange={(event) => onChange(ajustarLuminosidadeCor(value, Number(event.target.value)))}
          style={{ background: `linear-gradient(90deg, rgba(255,255,255,.92), ${value}, rgba(15,23,42,.72))` }}
          className="h-2 w-full cursor-pointer appearance-none rounded-full accent-[var(--fz-color-primary)] disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <input
        aria-label={field.label}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        className="w-full rounded-xl border border-[var(--fz-field-border)] bg-[var(--fz-field-bg)] px-3 py-2.5 font-mono text-xs text-[var(--fz-field-text)] outline-none transition-colors hover:border-[var(--fz-field-border-hover)] focus-visible:border-[var(--fz-field-border-focus)] focus-visible:ring-2 focus-visible:ring-[var(--fz-field-focus-ring)]"
        spellCheck={false}
      />

      {onSave ? (
        <div className="flex gap-2">
          {onReset ? (
            <Button variant="outline" size="sm" onClick={onReset} disabled={resetDisabled} className="min-w-0 flex-1">
              <Undo2 className="h-3.5 w-3.5" />
              Voltar
            </Button>
          ) : null}
          <Button variant="primary" size="sm" onClick={onSave} disabled={saveDisabled || Boolean(error)} className="min-w-0 flex-1">
            <Save className="h-3.5 w-3.5" />
            Salvar
          </Button>
        </div>
      ) : null}

      {error ? <span role="alert" className="col-span-full text-xs font-semibold text-[var(--fz-color-danger)]">{error}</span> : null}
    </div>
  );
}

function CampoTexto({
  label,
  description,
  value,
  placeholder,
  error,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  placeholder: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="mx-1 space-y-3 rounded-2xl border border-[var(--fz-color-border)] bg-[var(--fz-color-surface)] p-3 shadow-[0_6px_20px_rgba(15,23,42,0.04)]">
      <span>
        <span className="block text-sm font-bold text-[var(--fz-color-text)]">{label}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-[var(--fz-color-text-muted)]">{description}</span>
      </span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        className="w-full rounded-xl border border-[var(--fz-field-border)] bg-[var(--fz-field-bg)] px-3 py-2.5 text-sm text-[var(--fz-field-text)] outline-none transition-colors hover:border-[var(--fz-field-border-hover)] focus-visible:border-[var(--fz-field-border-focus)] focus-visible:ring-2 focus-visible:ring-[var(--fz-field-focus-ring)]"
        spellCheck={false}
      />
      {error ? <span role="alert" className="block text-xs font-semibold text-[var(--fz-color-danger)]">{error}</span> : null}
    </label>
  );
}

function ListaCamposCor({
  fields,
  valores,
  erros,
  dirtyTokens,
  onChange,
  onSave,
  onReset,
}: {
  fields: ColorField[];
  valores: ThemeAppearanceTokens;
  erros: ValidationErrors;
  dirtyTokens: Set<ThemeAppearanceToken>;
  onChange: (token: ThemeAppearanceToken, value: string) => void;
  onSave: (token: ThemeAppearanceToken) => void;
  onReset: (token: ThemeAppearanceToken) => void;
}) {
  return (
    <div className="space-y-2">
      {fields.map((field) => (
        <CampoCor
          key={field.token}
          field={field}
          value={valores[field.token]}
          error={erros[field.token]}
          onChange={(value) => onChange(field.token, value)}
          onSave={() => onSave(field.token)}
          onReset={() => onReset(field.token)}
          saveDisabled={!dirtyTokens.has(field.token)}
          resetDisabled={!dirtyTokens.has(field.token)}
        />
      ))}
    </div>
  );
}

function ButtonPreview() {
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia das variantes</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="primary">Principal</Button>
        <Button size="sm" variant="secondary">Secundário</Button>
        <Button size="sm" variant="outline">Contorno</Button>
        <Button size="sm" variant="ghost">Transparente</Button>
        <Button size="sm" variant="danger">Destrutivo</Button>
        <Button size="icon" variant="outline" aria-label="Exemplo de botão de ícone">
          <Paintbrush className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="primary" disabled>Desabilitado</Button>
      </div>
    </div>
  );
}

function SidebarPreview() {
  const impedirNavegacao = (event: MouseEvent<HTMLAnchorElement>) => event.preventDefault();

  return (
    <div className="space-y-3 rounded-2xl border border-[var(--fz-sidebar-border)] bg-[var(--fz-sidebar-bg)] p-3">
      <p className="text-xs font-semibold text-[var(--fz-sidebar-text)]">Prévia da navegação</p>
      <div className="space-y-1">
        <NavigationItem href="#" label="Início" Icon={Home} onClick={impedirNavegacao} />
        <NavigationItem
          href="#"
          label="Etiquetas"
          Icon={Tag}
          active
          indicator
          onClick={impedirNavegacao}
        />
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-[var(--fz-sidebar-divider)] pt-3">
        <NavigationItem compact href="#" label="Início" Icon={Home} onClick={impedirNavegacao} />
        <span className="rounded-full bg-[var(--fz-sidebar-badge-bg)] px-2 py-0.5 text-[10px] font-bold text-[var(--fz-sidebar-badge-text)]">3</span>
      </div>
    </div>
  );
}

function HeaderPreview() {
  return (
    <div className="rounded-2xl border border-[var(--fz-header-border)] bg-[var(--fz-header-bg)] p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-[var(--fz-header-text-muted)]">Bom dia,</p>
          <p className="truncate text-sm font-semibold text-[var(--fz-header-text)]">Nome do usuário</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Exemplo de notificações"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--fz-header-border)] bg-[var(--fz-header-action-bg)] text-[var(--fz-header-action-icon)] transition-colors hover:bg-[var(--fz-header-action-hover)] focus-visible:outline-2 focus-visible:outline-[var(--fz-header-action-icon)]"
          >
            <Bell className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Exemplo de configurações"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--fz-header-border)] bg-[var(--fz-header-action-bg)] text-[var(--fz-header-action-icon)] transition-colors hover:bg-[var(--fz-header-action-hover)] focus-visible:outline-2 focus-visible:outline-[var(--fz-header-action-icon)]"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldPreview() {
  return (
    <div className="space-y-3 rounded-2xl border border-[var(--fz-field-border)] bg-[var(--fz-field-bg)] p-3">
      <p className="text-xs font-semibold text-[var(--fz-field-text)]">Prévia dos estados dos campos</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input aria-label="Campo normal" defaultValue="Valor do campo" />
        <Input aria-label="Campo com texto de orientação" placeholder="Digite um valor" />
        <Input aria-label="Campo desabilitado" value="Desabilitado" disabled />
        <Input aria-label="Campo somente leitura" value="Somente leitura" readOnly />
        <Input aria-label="Campo com erro" value="Valor inválido" aria-invalid={true} />
      </div>
    </div>
  );
}

function CardPreview() {
  return (
    <div className="space-y-3 rounded-2xl border border-[var(--fz-card-accent)]/25 bg-[var(--fz-card-parent-bg)] p-3">
      <p className="text-xs font-semibold text-[var(--fz-card-value-color)]">Prévia de cards Pai e Filho</p>
      <div className="rounded-xl border border-[var(--fz-card-accent)]/20 bg-[var(--fz-card-child-bg)] p-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--fz-card-icon-bg)] text-[var(--fz-card-accent)]">
            <Paintbrush className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[var(--fz-card-value-color)]">Card filho</p>
            <p className="text-xs text-[var(--fz-card-value-color)]/70">Superfície interna do conteúdo.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChoicePreview() {
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia dos estados de seleção</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-sm text-navy">
          <Checkbox aria-label="Checkbox desmarcado" />
          Checkbox desmarcado
        </label>
        <label className="flex items-center gap-2 text-sm text-navy">
          <Checkbox aria-label="Checkbox marcado" defaultChecked />
          Checkbox marcado
        </label>
        <label className="flex items-center gap-2 text-sm text-navy">
          <Radio aria-label="Radio desmarcado" name="choice-preview" />
          Radio desmarcado
        </label>
        <label className="flex items-center gap-2 text-sm text-navy">
          <Radio aria-label="Radio marcado" name="choice-preview" defaultChecked />
          Radio marcado
        </label>
      </div>
    </div>
  );
}

function BadgeTypePreview() {
  const tipos = [
    { label: "Pago", bg: "--fz-badge-type-paid-bg", text: "--fz-badge-type-paid-text" },
    { label: "Personalizada", bg: "--fz-badge-type-personalized-bg", text: "--fz-badge-type-personalized-text" },
    { label: "Frio", bg: "--fz-badge-type-cold-bg", text: "--fz-badge-type-cold-text" },
    { label: "Perdido", bg: "--fz-badge-type-lost-bg", text: "--fz-badge-type-lost-text" },
  ];

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia dos tipos de etiqueta</p>
      <div className="flex flex-wrap gap-2">
        {tipos.map((tipo) => (
          <span key={tipo.label} className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase" style={{ backgroundColor: `var(${tipo.bg})`, color: `var(${tipo.text})` }}>{tipo.label}</span>
        ))}
      </div>
    </div>
  );
}

function BadgePreview() {
  const variantes = [
    { variant: "neutral" as const, label: "Neutro" },
    { variant: "info" as const, label: "Informação" },
    { variant: "success" as const, label: "Sucesso" },
    { variant: "warning" as const, label: "Aviso" },
    { variant: "danger" as const, label: "Erro" },
  ];

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia das variantes</p>
      <div className="flex flex-wrap items-center gap-2">
        {variantes.map(({ variant, label }) => (
          <Badge key={variant} variant={variant}>{label}</Badge>
        ))}
      </div>
    </div>
  );
}

function AlertPreview() {
  const variantes = [
    { variant: "info" as const, label: "Informação" },
    { variant: "success" as const, label: "Sucesso" },
    { variant: "warning" as const, label: "Aviso" },
    { variant: "danger" as const, label: "Erro" },
  ];

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia das variantes</p>
      <div className="grid gap-2">
        {variantes.map(({ variant, label }) => (
          <Alert key={variant} variant={variant}>{label}</Alert>
        ))}
      </div>
    </div>
  );
}

function LoadingPreview() {
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia do carregamento</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2 rounded-xl border border-border p-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <LoadingIndicator>Carregando dados...</LoadingIndicator>
      </div>
    </div>
  );
}

function TabsPreview() {
  const [value, setValue] = useState("resumo");

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia das abas</p>
      <Tabs value={value} onValueChange={setValue} className="space-y-3">
        <TabList aria-label="Prévia de abas" className="gap-7">
          <Tab value="resumo">Resumo</Tab>
          <Tab value="historico">Histórico</Tab>
        </TabList>
        <TabPanel value="resumo" className="rounded-xl border border-border p-3 text-sm text-navy">
          Conteúdo resumido
        </TabPanel>
        <TabPanel value="historico" className="rounded-xl border border-border p-3 text-sm text-navy">
          Conteúdo histórico
        </TabPanel>
      </Tabs>
    </div>
  );
}

function FloatingPreview() {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia de menus e painéis flutuantes</p>
      <div className="flex flex-wrap gap-2">
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          trigger={(triggerProps) => (
            <Button {...triggerProps} variant="outline" size="sm">
              Abrir painel
            </Button>
          )}
          contentClassName="mt-2 w-56 rounded-xl p-3 text-sm"
          contentAriaLabel="Prévia de painel flutuante"
        >
          Este é um painel flutuante.
        </Popover>
        <Menu
          open={menuOpen}
          onOpenChange={setMenuOpen}
          ariaLabel="Prévia de menu"
          trigger={(triggerProps) => (
            <Button {...triggerProps} variant="secondary" size="sm">
              Abrir menu
            </Button>
          )}
          contentClassName="mt-2 w-44"
        >
          <MenuItem className="gap-2 px-3 py-2 text-sm">Editar</MenuItem>
          <MenuItem className="gap-2 px-3 py-2 text-sm">Duplicar</MenuItem>
        </Menu>
      </div>
    </div>
  );
}

function OverlayPreview() {
  const previewId = useId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dialogTitleId = `theme-overlay-dialog-title-${previewId}`;
  const dialogDescriptionId = `theme-overlay-dialog-description-${previewId}`;
  const drawerTitleId = `theme-overlay-drawer-title-${previewId}`;
  const drawerDescriptionId = `theme-overlay-drawer-description-${previewId}`;

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-white p-3">
      <p className="text-xs font-semibold text-muted">Prévia de janelas e painéis laterais</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
          Abrir janela
        </Button>
        <Button variant="secondary" size="sm" onClick={() => setDrawerOpen(true)}>
          Abrir painel lateral
        </Button>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
      >
        <h4 id={dialogTitleId} className="text-base font-semibold">Exemplo de janela</h4>
        <p id={dialogDescriptionId} className="mt-2 text-sm opacity-80">
          Prévia da superfície de uma janela modal.
        </p>
        <div className="mt-5 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>
            Fechar
          </Button>
        </div>
      </Dialog>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        placement="right"
        aria-labelledby={drawerTitleId}
        aria-describedby={drawerDescriptionId}
      >
        <div className="space-y-4 p-6">
          <div>
            <h4 id={drawerTitleId} className="text-base font-semibold">Exemplo de painel lateral</h4>
            <p id={drawerDescriptionId} className="mt-2 text-sm opacity-80">
              Prévia da superfície de um painel lateral.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDrawerOpen(false)}>
            Fechar
          </Button>
        </div>
      </Drawer>
    </div>
  );
}

export default function ThemeAppearanceEditor() {
  const [savedOverrides, setSavedOverrides] = useState<ThemeAppearanceOverrides>({});
  const [previewOverrides, setPreviewOverrides] = useState<ThemeAppearanceOverrides>({});
  const [dirtyTokens, setDirtyTokens] = useState<Set<ThemeAppearanceToken>>(new Set());
  const [valoresEfetivos, setValoresEfetivos] = useState<ThemeAppearanceTokens | null>(null);
  const [valoresCamposEstado, setValoresCamposEstado] = useState<ThemeAppearanceTokens | null>(null);
  const [editando, setEditando] = useState(false);
  const [errosValidacao, setErrosValidacao] = useState<ValidationErrors>({});
  const [mensagem, setMensagem] = useState("");

  const savedRef = useRef<ThemeAppearanceOverrides>({});
  const previewRef = useRef<ThemeAppearanceOverrides>({});
  const dirtyRef = useRef<Set<ThemeAppearanceToken>>(new Set());
  const efetivosRef = useRef<ThemeAppearanceTokens | null>(null);
  const camposRef = useRef<ThemeAppearanceTokens | null>(null);
  const errosRef = useRef<ValidationErrors>({});

  const valoresCampos = valoresCamposEstado ?? DEFAULT_THEME_APPEARANCE.tokens;

  function atualizarSaved(overrides: ThemeAppearanceOverrides) {
    savedRef.current = overrides;
    setSavedOverrides(overrides);
  }

  function atualizarPreview(overrides: ThemeAppearanceOverrides) {
    previewRef.current = overrides;
    setPreviewOverrides(overrides);
  }

  function atualizarDirty(tokens: Set<ThemeAppearanceToken>) {
    dirtyRef.current = tokens;
    setDirtyTokens(tokens);
  }

  function atualizarErros(errors: ValidationErrors) {
    errosRef.current = errors;
    setErrosValidacao(errors);
  }

  function atualizarCampos(valores: ThemeAppearanceTokens) {
    camposRef.current = valores;
    setValoresCamposEstado(valores);
  }

  const sincronizarValoresEfetivos = useCallback(() => {
    const resultado = lerThemeAppearanceOverrides();
    const salvos = resultado.source === "v2-invalid" ? {} : resultado.overrides;
    savedRef.current = salvos;
    setSavedOverrides(salvos);

    const protegidos = new Set<ThemeAppearanceToken>([
      ...(Object.keys(salvos) as ThemeAppearanceToken[]),
      ...(Object.keys(previewRef.current) as ThemeAppearanceToken[]),
    ]);
    const efetivos = lerValoresEfetivos(efetivosRef.current, protegidos);
    efetivosRef.current = efetivos;
    setValoresEfetivos(efetivos);

    const compostos = comporThemeAppearanceValores(efetivos, salvos, previewRef.current);
    const atuais = camposRef.current;
    if (!atuais) {
      camposRef.current = compostos;
      setValoresCamposEstado(compostos);
      return;
    }

    const proximos = { ...atuais };
    for (const token of Object.keys(THEME_APPEARANCE_CSS_VARIABLES) as ThemeAppearanceToken[]) {
      if (!dirtyRef.current.has(token) && !errosRef.current[token]) proximos[token] = compostos[token];
    }
    camposRef.current = proximos;
    setValoresCamposEstado(proximos);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(sincronizarValoresEfetivos);

    const tratarStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== THEME_APPEARANCE_STORAGE_KEY && event.key !== THEME_APPEARANCE_STORAGE_KEY_V2) return;
      atualizarPreview({});
      atualizarDirty(new Set());
      atualizarErros({});
      sincronizarValoresEfetivos();
    };
    const tratarViewport = () => sincronizarValoresEfetivos();
    const tratarTema = () => sincronizarValoresEfetivos();
    const observer = new MutationObserver(() => sincronizarValoresEfetivos());
    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["data-theme", "data-theme-role", "data-fz-appearance"],
    });

    window.addEventListener("storage", tratarStorage);
    window.addEventListener("resize", tratarViewport);
    window.addEventListener("bp-theme-change", tratarTema);
    window.addEventListener("bp-auth-role-change", tratarTema);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("storage", tratarStorage);
      window.removeEventListener("resize", tratarViewport);
      window.removeEventListener("bp-theme-change", tratarTema);
      window.removeEventListener("bp-auth-role-change", tratarTema);
    };
  }, [sincronizarValoresEfetivos]);

  function iniciarEdicao() {
    const efetivos = efetivosRef.current ?? lerValoresEfetivos(null, new Set());
    efetivosRef.current = efetivos;
    setValoresEfetivos(efetivos);
    atualizarPreview({});
    atualizarDirty(new Set());
    atualizarErros({});
    atualizarCampos(comporThemeAppearanceValores(efetivos, savedRef.current, {}));
    setMensagem("");
    setEditando(true);
  }

  function atualizarToken(token: ThemeAppearanceToken, valor: string) {
    const camposAtuais = camposRef.current ?? valoresCampos;
    atualizarCampos({ ...camposAtuais, [token]: valor });
    const normalizado = normalizarThemeAppearanceOverrides({ [token]: valor });
    if (!normalizado) {
      atualizarErros({ ...errosRef.current, [token]: mensagemValidacao(token) });
      return;
    }

    const valorNormalizado = normalizado[token];
    if (!valorNormalizado) return;
    const base = savedRef.current[token] ?? efetivosRef.current?.[token] ?? DEFAULT_THEME_APPEARANCE.tokens[token];
    const proximos = { ...previewRef.current };
    if (valorNormalizado === base) delete proximos[token];
    else proximos[token] = valorNormalizado;
    atualizarPreview(proximos);
    atualizarDirty(new Set(Object.keys(proximos) as ThemeAppearanceToken[]));
    atualizarErros(Object.fromEntries(Object.entries(errosRef.current).filter(([chave]) => chave !== token)) as ValidationErrors);
    atualizarCampos({ ...(camposRef.current ?? valoresCampos), [token]: valorNormalizado });
    setMensagem("Prévia ativa. Salve para manter neste navegador.");
    solicitarPreviewThemeAppearance(proximos);
  }

  function voltarToken(token: ThemeAppearanceToken) {
    if (!dirtyRef.current.has(token)) return;

    const valorSalvo = savedRef.current[token];
    const proximosPreview = removerThemeAppearanceOverride(previewRef.current, token);
    const proximosDirty = new Set(dirtyRef.current);
    proximosDirty.delete(token);
    const proximosErros = { ...errosRef.current };
    delete proximosErros[token];
    const valorRestaurado = valorSalvo ?? efetivosRef.current?.[token] ?? DEFAULT_THEME_APPEARANCE.tokens[token];

    atualizarPreview(proximosPreview);
    atualizarDirty(proximosDirty);
    atualizarErros(proximosErros);
    atualizarCampos({ ...(camposRef.current ?? valoresCampos), [token]: valorRestaurado });
    setMensagem(valorSalvo ? "Alteração desfeita. Valor salvo restaurado." : "Alteração desfeita. Valor herdado restaurado.");
    solicitarPreviewThemeAppearance(proximosPreview);
  }

  function cancelarEdicao() {
    solicitarCancelarThemeAppearance();
    atualizarPreview({});
    atualizarDirty(new Set());
    atualizarErros({});
    const efetivos = efetivosRef.current ?? lerValoresEfetivos(null, new Set());
    atualizarCampos(comporThemeAppearanceValores(efetivos, savedRef.current, {}));
    setMensagem("Alterações canceladas.");
    setEditando(false);
  }

  function salvarToken(token: ThemeAppearanceToken) {
    if (errosRef.current[token]) return;
    const valor = previewRef.current[token];
    if (!valor) return;

    const override = { [token]: valor } as ThemeAppearanceOverrides;
    solicitarSalvarThemeAppearance(override);
    const novosSalvos = mesclarThemeAppearanceOverrides(savedRef.current, override);
    const proximaPreview = { ...previewRef.current };
    delete proximaPreview[token];
    const proximosDirty = new Set(dirtyRef.current);
    proximosDirty.delete(token);
    atualizarSaved(novosSalvos);
    atualizarPreview(proximaPreview);
    atualizarDirty(proximosDirty);
    atualizarCampos(comporThemeAppearanceValores(efetivosRef.current ?? DEFAULT_THEME_APPEARANCE.tokens, novosSalvos, proximaPreview));
    setMensagem("Alteração salva somente neste navegador Beta.");
  }

  function salvarEdicao() {
    if (Object.keys(errosRef.current).length > 0) return;
    const overridesParaSalvar = Object.fromEntries(
      [...dirtyTokens]
        .filter((token) => previewRef.current[token] !== undefined)
        .map((token) => [token, previewRef.current[token]]),
    ) as ThemeAppearanceOverrides;
    solicitarSalvarThemeAppearance(overridesParaSalvar);
    const novosSalvos = mesclarThemeAppearanceOverrides(savedRef.current, overridesParaSalvar);
    atualizarSaved(novosSalvos);
    atualizarPreview({});
    atualizarDirty(new Set());
    atualizarCampos(comporThemeAppearanceValores(efetivosRef.current ?? DEFAULT_THEME_APPEARANCE.tokens, novosSalvos, {}));
    setMensagem("Aparência salva somente neste navegador Beta.");
    setEditando(false);
  }

  function restaurarPadrao() {
    solicitarRestaurarThemeAppearance();
    atualizarSaved({});
    atualizarPreview({});
    atualizarDirty(new Set());
    atualizarErros({});
    const efetivos = lerValoresEfetivos(null, new Set());
    efetivosRef.current = efetivos;
    setValoresEfetivos(efetivos);
    atualizarCampos(efetivos);
    setMensagem("Padrão restaurado. A aparência personalizada foi removida.");
  }

  const valoresResumo = valoresEfetivos
    ? comporThemeAppearanceValores(valoresEfetivos, savedOverrides, previewOverrides)
    : valoresCampos;

  return (
    <div className="overflow-hidden rounded-[28px] border border-[var(--fz-color-border)] bg-[var(--fz-color-background)] shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
      <div className="flex items-start gap-3 border-b border-[var(--fz-color-border)] bg-[var(--fz-color-surface)] px-5 py-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--fz-color-primary)]/10 text-[var(--fz-color-primary)]">
          <Paintbrush className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-bold text-[var(--fz-color-text)]">Editor de aparência</h3>
            <span className="rounded-full bg-[var(--fz-color-primary)]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[var(--fz-color-primary)]">
              Experimental · Beta
            </span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
            Ajustes visuais salvos somente neste navegador. O modo escuro continua usando o tema próprio.
          </p>
        </div>
      </div>

      {!editando ? (
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Principal", value: valoresResumo.primary },
              { label: "Fundo", value: valoresResumo.background },
              { label: "Superfície", value: valoresResumo.surface },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-border bg-white p-2.5">
                <input
                  aria-label={`Amostra de ${item.label}`}
                  type="color"
                  value={valorParaSeletorDeCor(item.value)}
                  disabled
                  className="h-8 w-full rounded-xl border border-border bg-white p-1"
                />
                <p className="mt-2 text-[11px] font-semibold text-muted">{item.label}</p>
                <p className="truncate font-mono text-[10px] text-navy">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted">
              Base {valoresResumo["font-size-base"]} · raio {valoresResumo.radius}
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={iniciarEdicao}
            >
              <Paintbrush className="h-4 w-4" />
              Editar aparência
            </Button>
          </div>
          {mensagem ? <p className="text-xs font-semibold text-brand">{mensagem}</p> : null}
        </div>
      ) : (
        <div className="space-y-5 p-5">
          <div className="flex items-start gap-2 rounded-2xl border border-[var(--fz-color-primary)]/20 bg-[var(--fz-color-primary)]/5 px-3 py-2.5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fz-color-primary)]" />
            <p className="text-xs leading-relaxed text-[var(--fz-color-primary)]">
              A prévia é aplicada imediatamente. Salvar persiste neste navegador; cancelar descarta a prévia.
            </p>
          </div>

          {COLOR_GROUPS.map((group) => (
            <EditorModulo key={group.title} title={group.title} open={group.title === "Cores globais"}>
              <ListaCamposCor
                fields={group.fields}
                valores={valoresCampos}
                erros={errosValidacao}
                dirtyTokens={dirtyTokens}
                onChange={atualizarToken}
                onSave={salvarToken}
                onReset={voltarToken}
              />
            </EditorModulo>
          ))}

          <EditorModulo title="Tipografia e formato">
            <div className="space-y-2">
              <CampoTexto
                label="Família tipográfica"
                description="Use nomes separados por vírgula e fallback do sistema."
                value={valoresCampos["font-family"]}
                placeholder="Inter, system-ui, sans-serif"
                error={errosValidacao["font-family"]}
                onChange={(value) => atualizarToken("font-family", value)}
              />
              <CampoTexto
                label="Tamanho base"
                description="Exemplo: 1rem. Afeta somente áreas migradas."
                value={valoresCampos["font-size-base"]}
                placeholder="1rem"
                error={errosValidacao["font-size-base"]}
                onChange={(value) => atualizarToken("font-size-base", value)}
              />
              <CampoTexto
                label="Arredondamento"
                description="Exemplo: 0.75rem. Afeta somente áreas migradas."
                value={valoresCampos.radius}
                placeholder="0.75rem"
                error={errosValidacao.radius}
                onChange={(value) => atualizarToken("radius", value)}
              />
            </div>
          </EditorModulo>

          <EditorModulo title="Botões" preview={<ButtonPreview />}>
            <ListaCamposCor
              fields={BUTTON_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
            <div className="space-y-2">
              {BUTTON_VALUE_FIELDS.map((field) => (
                <CampoTexto
                  key={field.token}
                  label={field.label}
                  description={field.description}
                  value={valoresCampos[field.token]}
                  placeholder={field.placeholder}
                  error={errosValidacao[field.token]}
                  onChange={(value) => atualizarToken(field.token, value)}
                />
              ))}
            </div>
          </EditorModulo>

          <EditorModulo title="Navegação" preview={<SidebarPreview />}>
            <ListaCamposCor
              fields={SIDEBAR_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Cabeçalho" preview={<HeaderPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">Cabeçalho atual — controla somente os tokens da família <code>--fz-header-*</code>.</p>
            <ListaCamposCor
              fields={HEADER_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Cards" preview={<CardPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente os fundos, destaques, ícones e valores da família Card. Borda e arredondamento continuam nos controles globais.
            </p>
            <ListaCamposCor
              fields={CARD_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Campos" preview={<FieldPreview />}>
            <ListaCamposCor
              fields={FIELD_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
            <CampoTexto
              label="Arredondamento"
              description="Raio dos campos. Exemplo: 0.75rem."
              value={valoresCampos["field-radius"]}
              placeholder="0.75rem"
              error={errosValidacao["field-radius"]}
              onChange={(value) => atualizarToken("field-radius", value)}
            />
          </EditorModulo>

          <EditorModulo title="Checkbox e Radio" preview={<ChoicePreview />}>
            <ListaCamposCor
              fields={CHOICE_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
            <CampoTexto
              label="Opacidade desabilitado"
              description="Valor entre 0 e 1 para caixas de seleção e opções indisponíveis."
              value={valoresCampos["choice-disabled-opacity"]}
              placeholder="0.5"
              error={errosValidacao["choice-disabled-opacity"]}
              onChange={(value) => atualizarToken("choice-disabled-opacity", value)}
            />
          </EditorModulo>

          <EditorModulo title="Tipos de etiqueta" preview={<BadgeTypePreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente a aparência do tipo semântico exibido na coluna TIPO: Pago, Personalizada, Frio e Perdido. As cores individuais das etiquetas permanecem funcionais.
            </p>
            <ListaCamposCor
              fields={BADGE_TYPE_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Status e selos" preview={<BadgePreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente os selos genéricos por semântica: neutro, informação, sucesso, aviso e erro. Tipos e cores individuais de etiquetas permanecem separados.
            </p>
            <ListaCamposCor
              fields={BADGE_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Alertas e feedback" preview={<AlertPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente alertas genéricos de informação, sucesso, aviso e erro. Selos, etiquetas e validações de campos permanecem separados.
            </p>
            <ListaCamposCor
              fields={ALERT_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Carregamento" preview={<LoadingPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente indicadores visuais de carregamento e elementos genéricos. O carregamento de botões, uploads, mídia e processos específicos permanece separado.
            </p>
            <ListaCamposCor
              fields={LOADING_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Abas" preview={<TabsPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente a aparência e o foco das abas padronizadas. Filtros, alternadores e seções recolhíveis permanecem separados.
            </p>
            <ListaCamposCor
              fields={TABS_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
          </EditorModulo>

          <EditorModulo title="Menus e painéis flutuantes" preview={<FloatingPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente superfícies de menus e painéis flutuantes padronizados. Dicas de contexto, modais e listas suspensas especiais permanecem separadas.
            </p>
            <ListaCamposCor
              fields={FLOATING_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
            <div className="space-y-2">
              {FLOATING_VALUE_FIELDS.map((field) => (
                <CampoTexto
                  key={field.token}
                  label={field.label}
                  description={field.description}
                  value={valoresCampos[field.token]}
                  placeholder={field.placeholder}
                  error={errosValidacao[field.token]}
                  onChange={(value) => atualizarToken(field.token, value)}
                />
              ))}
            </div>
          </EditorModulo>

          <EditorModulo title="Janelas e painéis" preview={<OverlayPreview />}>
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">
              Controla somente as superfícies padronizadas de janelas e painéis laterais. Menus flutuantes e sobreposições especiais permanecem separados.
            </p>
            <ListaCamposCor
              fields={OVERLAY_COLOR_FIELDS}
              valores={valoresCampos}
              erros={errosValidacao}
              dirtyTokens={dirtyTokens}
              onChange={atualizarToken}
              onSave={salvarToken}
              onReset={voltarToken}
            />
            <div className="space-y-2">
              {OVERLAY_VALUE_FIELDS.map((field) => (
                <CampoTexto
                  key={field.token}
                  label={field.label}
                  description={field.description}
                  value={valoresCampos[field.token]}
                  placeholder={field.placeholder}
                  error={errosValidacao[field.token]}
                  onChange={(value) => atualizarToken(field.token, value)}
                />
              ))}
            </div>
          </EditorModulo>

          <EditorModulo title="Compatibilidade / Legado">
            <p className="text-xs leading-relaxed text-[var(--fz-color-text-muted)]">Controles mantidos para áreas que ainda não foram migradas para o novo sistema de design.</p>
            <CampoCor
              field={{
                token: "header",
                label: "Cabeçalho legado",
                description: "Usado somente por cabeçalhos e superfícies ainda não migrados para o novo sistema de design.",
              }}
              value={valoresCampos.header}
              error={errosValidacao.header}
              onChange={(value) => atualizarToken("header", value)}
              onSave={() => salvarToken("header")}
              onReset={() => voltarToken("header")}
              saveDisabled={!dirtyTokens.has("header")}
              resetDisabled={!dirtyTokens.has("header")}
            />
          </EditorModulo>

          {mensagem ? <p className="text-xs font-semibold text-brand">{mensagem}</p> : null}

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button
              variant="outline"
              size="md"
              onClick={restaurarPadrao}
            >
              <RotateCcw className="h-4 w-4" />
              Restaurar padrão
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={cancelarEdicao}
            >
              <X className="h-4 w-4" />
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={salvarEdicao}
              disabled={Object.keys(errosValidacao).length > 0}
              className="ml-auto"
            >
              <Save className="h-4 w-4" />
              Salvar aparência
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
